import { memo } from 'react';
import { Navigate, Route, Routes, useNavigate, useResolvedPath } from 'react-router-dom';
import { IAppointment, insertAuditTrail, updateAppointmentKeyStatus } from '../../../../API/GoMoto';
import { Events } from '../../../../API/Enums';
import {
    AppointmentDispatchTypes,
    useAppointmentDispatch,
    useAppointmentState,
    useAppointmentUpdateAndPersist,
    useTowInProcess,
} from '../../../../context/Appointment/appointment';
import { FinishAtLocker } from './FinishAtLocker/FinishAtLocker';
import { FinishWithAdvisor } from './FinishWithAdvisor/FinishWithAdvisor';
import { LockerOpenIssue } from './LockerOpenIssue';
import { Appointment_KeydropRoutePaths, PathName } from '../../../../helpers/Routes';
import store from 'store2';
import { AdvisorOption } from './AdvisorOption/AdvisorOption';
import { GMDoubleCheckLocker } from '../../../../controls/DoubleCheckLocker';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { OutdoorKeyDropRoutesProps } from './OutdoorKeyDropRoutes.types';

export const OutdoorKeyDropRoutes: GoMotoComponent<OutdoorKeyDropRoutesProps> = memo(({
    onProceed,
}) => {
    const { pathname } = useResolvedPath('.');
    const navigate = useNavigate();
    const saveAppointment = useAppointmentUpdateAndPersist();
    const appointmentDispatch = useAppointmentDispatch();
    const appointment = useAppointmentState();
    const isTowIn = useTowInProcess();

    const onLockerClosed: VoidFunction = async () => {
        const tempAppt: IAppointment = store.session.remove('tempAppointment') ?? appointment;
        await saveAppointment({
            ...tempAppt,
            keyStatus: tempAppt.keySlotNumber ? 'slot_assigned' : tempAppt.keyStatus,
            allowManualComplete: false,
            status: 'scheduled',
        });
        onProceed();
    };

    const onReopenLocker: VoidFunction = async () => {
        const tempAppt: IAppointment = store.session.get('tempAppointment') ?? appointment;
        await updateAppointmentKeyStatus(tempAppt.key, 'reopen requested dropoff');
        appointmentDispatch({
            type: AppointmentDispatchTypes.Update,
            payload: {
                ...tempAppt,
                keyStatus: 'reopen requested dropoff',
            },
        });
        navigate(PathName(pathname, Appointment_KeydropRoutePaths.Locker));
    };

    return (
        <Routes>
            <Route
                path={PathName(Appointment_KeydropRoutePaths.Option, '*')}
                element={
                    <AdvisorOption
                        onSkip={() => {
                            const tempAppt: IAppointment = store.session.get('tempAppointment') ?? appointment;
                            store.session.set(
                                'tempAppointment',
                                {
                                    ...appointment,
                                    kioskKey: tempAppt.kioskKey,
                                },
                            );
                            navigate(PathName(pathname, Appointment_KeydropRoutePaths.Locker), { replace: true });
                        }}
                        onUseLocker={() => {
                            const tempAppt: IAppointment = store.session.get('tempAppointment') ?? appointment;
                            store.session.set(
                                'tempAppointment',
                                {
                                    ...appointment,
                                    kioskKey: tempAppt.kioskKey,
                                },
                            );
                            navigate(PathName(pathname, Appointment_KeydropRoutePaths.Locker));
                        }}
                        onSeeAdvisor={() => navigate(PathName(pathname, Appointment_KeydropRoutePaths.Advisor))}
                    />
                }
            />
            <Route
                path={PathName(Appointment_KeydropRoutePaths.Locker, '*')}
                element={
                    <FinishAtLocker
                        appointment={store.session.get('tempAppointment')}
                        onLockerAssigned={isTowIn ? async () => {
                            const currentSession: IAppointment = store.session.has('tempAppointment')
                                ? store.session.get('tempAppointment')
                                : appointment;
                            const scheduledAppt: IAppointment = {
                                ...currentSession,
                                allowManualComplete: false,
                                status: 'scheduled',
                            };
                            store.session.set('tempAppointment', scheduledAppt);
                            await saveAppointment(scheduledAppt);
                            if (!store.session.get('checkInCompleteLogged')) {
                                await insertAuditTrail(scheduledAppt.key, Events.Check_In_Complete);
                                store.session.set('checkInCompleteLogged', true);
                            }
                        } : undefined}
                        onLockerClosed={async (lockerNumber: number, kioskKey: string, lockerId: number | undefined) => {
                            const currentSession: IAppointment = store.session.has('tempAppointment')
                                ? store.session.get('tempAppointment')
                                : appointment;
                            const tempAppointment: IAppointment = {
                                ...currentSession,
                                kioskKey: kioskKey,
                                keySlotNumber: lockerNumber ?? 0,
                                keySlot: lockerId ?? 0,
                            };
                            store.session.set('tempAppointment', tempAppointment);
                            if (isTowIn) {
                                await saveAppointment({
                                    ...tempAppointment,
                                    keyStatus: tempAppointment.keySlotNumber ? 'slot_assigned' : tempAppointment.keyStatus,
                                });
                            }
                            navigate(PathName(pathname, Appointment_KeydropRoutePaths.DoubleCheck));
                        }}
                        onLockerDidntOpen={() => navigate(PathName(pathname, Appointment_KeydropRoutePaths.Help))}
                    />
                }
            />
            <Route
                path={PathName(Appointment_KeydropRoutePaths.DoubleCheck, '*')}
                element={
                    <GMDoubleCheckLocker
                        lockerOperationType={'drop-off'}
                        onContinue={() => {
                            onLockerClosed();
                        }}
                        onReopenLocker={onReopenLocker}
                    />
                }
            />
            <Route
                path={PathName(Appointment_KeydropRoutePaths.Advisor, '*')}
                element={
                    <FinishWithAdvisor
                        onAdvisorCompleted={() => {
                            store.session.remove('tempAppointment');
                            onProceed();
                        }}
                    />
                }
            />
            <Route
                path={PathName(Appointment_KeydropRoutePaths.Help, '*')}
                element={
                    <LockerOpenIssue
                        onSeeAdvisor={() => navigate(PathName(pathname, Appointment_KeydropRoutePaths.Advisor))}
                        onTryAgain={() => navigate(PathName(pathname, Appointment_KeydropRoutePaths.Locker))}
                    />
                }
            />
            <Route path={'*'} element={<Navigate replace to={PathName(pathname, Appointment_KeydropRoutePaths.Option)} />} />
        </Routes>
    );
});
